const study = {
  meta: {
    slug: "hvac-contractor-185k-summer-crunch",
    title:
      "$185K in 22 hours: How an Illinois HVAC contractor cleared the summer install crunch",
    excerpt:
      "An 8-year residential HVAC contractor. 47% YoY revenue growth. But a June install queue demanded $180K in equipment inventory before commissions cleared. Here's how we structured the file.",
    industry: "HVAC / residential trades",
    product: "Working Capital + Revenue Financing (hybrid)",
    amount: "$185K",
    term: "9 months",
    timeToFund: "22 hours",
    state: "Illinois",
    challenge: "Seasonal cash flow gap",
    image:
      "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1600",
    date: "2026-05-14",
    readTime: "5 min read",
    tags: ["working-capital"],
    relatedArticles: [
      "working-capital-vs-line-of-credit",
      "combining-working-capital-and-line-of-credit",
      "time-in-business-funding-options",
    ],
  },
  situation: `The owner walked in with a common summer story: 47% YoY revenue growth, a fully booked June install calendar, and a working capital problem that math couldn't ignore.

Twenty-three units — mostly high-SEER heat pumps — needed to be ordered from the distributor by the 15th to hit the install dates his crew had promised. The distributor wanted net-15 on the entire pallet: **$180,000 in equipment cost**, due before a single install was invoiced.

His numbers were solid. Trailing 12 months of business bank statements showed $2.1M in deposits, average daily balance of $28K, and a 680 personal FICO. Two years earlier the same file would've walked straight into an SBA 7(a) — but SBA 7(a) takes 45–60 days, and he had 8.

We needed capital in the door before the 15th.`,
  structure: `**The layered approach.** No single product was going to close this deal cleanly. A pure MCA would've priced too high for a 9-month season; a term loan too slow; a line of credit alone wouldn't stretch to $185K on his profile.

We split the file across two lenders:

- **Fintech Working Capital · $110K** — 9-month term, 1.19 factor. Funded in 22 hours via next-day ACH after 4 months of bank statements + a signed application.
- **Revenue Financing · $75K** — Repaid as 8% of daily card revenue, capped at 12 months. Second-position, but structured so the two payments together stayed under 18% of average daily deposits.

**Total blended cost of capital: ~34% APR effective.** More expensive than SBA, dramatically cheaper than a pure MCA, and — critically — the paperwork existed in the borrower's own online banking. No CPA prep. No tax return sourcing. No collateral filings.

**What we intentionally didn't do:** we didn't stack a third product ("cash on cash on cash" is how underwriters describe the file we said no to). We didn't offer a personal guarantee on the fintech piece (the profile didn't require it). We didn't route through an SBA Express — that lender's timeline was closer to 3 weeks and would have missed the distributor deadline.`,
  outcome: `The wire hit the operating account at 11:47 AM the next business day. The equipment order went in at 2:15 PM. First install was on the 18th.

By the end of the season:

- **$185K deployed against $412K in installed revenue** (2.2× turn on the borrowed capital)
- **Blended debt service** consumed 11% of monthly deposits — well inside the 20% we consider healthy
- **Paid off in 9 months**, on schedule, no missed debits
- **Refi opportunity opened** in month 10 — with a season of solid repayment history, the same borrower now qualifies for an SBA 7(a) working line of credit at ~11% APR for the next expansion

The lesson we file this one under: **the right product is often two products, structured against different revenue streams**. A single-product answer is the easy one; the correct one usually requires a broker who's willing to read the file twice.`,
};

export default study;
